import { useState } from "react";
import { Badge, Col, Row } from "react-bootstrap";
import { CommonButton, CommonModal } from "../Input";
import DataTable from "../table/DataTable";
import Swal from "sweetalert2";

const RequestInformationModal = ({ show, onHide, request, isUpdate }) => {
  const [notes, setNotes] = useState("");

  if (!request) return null;
  const userRole = localStorage.getItem("userRole");
  
  const isManagerAndNew =
    userRole === "Vault Manager" && request.statusValue?.toLowerCase() === "new";
  
  const isSupervisorAndCashDrawer = 
    userRole === "Supervisor" && request.typeValue === "updateCashDrawer" && request.statusValue?.toLowerCase() === "new";

  const canShowActions = isUpdate && (isManagerAndNew || isSupervisorAndCashDrawer);

  const isSupervisorAndNotes =
    (userRole === "Supervisor" || userRole === "Vault Manager") &&
    request.notes;

  const handleApprove = () => {
    Swal.fire({
      icon: "success",
      title: "Approved",
      text: "Request has been approved successfully!",
      confirmButtonColor: "var(--primary)",
    }).then(() => {
      onHide();
    });
  };

  const handleReject = () => {
    Swal.fire({
      icon: "error",
      title: "Rejected",
      text: "Request has been rejected successfully!",
      confirmButtonColor: "var(--primary)",
    }).then(() => {
      onHide();
    });
  };

  const summaryData = [
    { label: "Drawer ID", value: request.drawerId },
    { label: "Created Date", value: `${request.displayDate} ${request.time}` },
    { label: "Created By", value: request.createdBy },
    { label: "Type", value: request.type },
    { label: "Status", value: request.status },
    { label: "Currency", value: request.currency },
  ];

  const columns = [
    {
      label: "IMAGE",
      key: "image",
      className: "ps-4 text-center",
      cellClassName: "ps-4 text-center",
      style: { width: "100px" },
      render: (item) => (
        <div
          className="d-flex align-items-center justify-content-center border rounded bg-white overflow-hidden mx-auto"
          style={{ width: "45px", height: "30px", borderColor: "#eee" }}
        >
          {item.image ? (
            <img
              src={item.image}
              alt={item.label}
              style={{ width: "100%", height: "auto" }}
            />
          ) : (
            <div className="text-muted fs-8 fw-bold">$</div>
          )}
        </div>
      ),
    },
    {
      label: "DENOMINATION",
      key: "label",
      style: { width: "250px" },
      render: (item) => (
        <span className="fs-13 fw-bold text-black">{item.label}</span>
      ),
    },
    {
      label: "INVENTORY",
      key: "inventory",
      className: "text-center",
      cellClassName: "text-center",
      style: { width: "150px" },
      render: (item) => (
        <div className="d-flex align-items-center justify-content-center">
          {item.inventory !== "-" && (
            <div
              className="rounded-circle me-1"
              style={{
                width: "8px",
                height: "8px",
                backgroundColor: "#34D399",
              }}
            />
          )}
          <Badge
            bg="light"
            className="text-black fw-bold px-3 py-1 border rounded-pill"
            style={{ fontSize: "11px", backgroundColor: "#F9FAFB" }}
          >
            {item.inventory}
          </Badge>
        </div>
      ),
    },
    {
      label: "QUANTITY",
      key: "quantity",
      className: "text-center",
      cellClassName: "text-center",
      style: { width: "200px" },
      render: (item) => (
        <div className="d-flex justify-content-center">
          <div
            className="form-control text-center py-1 d-flex align-items-center justify-content-center"
            style={{
              height: "36px",
              width: "100px",
              fontSize: "13px",
              borderColor: "#E2E8F0",
              borderRadius: "8px",
              backgroundColor: "#fff",
            }}
          >
            {item.qty}
          </div>
        </div>
      ),
    },
    {
      label: "TOTAL",
      key: "total",
      className: "pe-4 text-end",
      cellClassName: "pe-4 text-end",
      style: { width: "180px" },
      render: (item) => (
        <div className="fw-bold fs-13 text-black">
          SGD{" "}
          {(item.value * item.qty).toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })}
        </div>
      ),
    },
  ];

  return (
    <CommonModal
      show={show}
      onHide={onHide}
      title="Request Information"
      fullscreen={true}
      headerClassName="px-4"
      footerClassName={
        canShowActions
          ? "bg-light-gray-soft pb-4 pt-0 pe-5 border-top-0 d-flex justify-content-end"
          : undefined
      }
      footer={
        canShowActions ? (
          <div className="d-flex gap-3">
            <CommonButton
              variant="outline"
              className="px-5 bg-white border"
              style={{
                fontWeight: "600",
                borderRadius: "8px",
              }}
              onClick={handleReject}
            >
              <i className="fa fa-times me-2"></i> Reject
            </CommonButton>
            <CommonButton
              className="px-5 fw-bold"
              style={{
                borderRadius: "8px",
              }}
              onClick={handleApprove}
            >
              <i className="fa fa-check me-2"></i> Approve
            </CommonButton>
          </div>
        ) : null
      }
    >
      <div>
        <label className="text-black fw-bold fs-12 mb-3">
          Request Information
        </label>
        <div
          className="p-4 mb-3 border border-light"
          style={{ borderRadius: "12px", backgroundColor: "#FAFAFA" }}
        >
          <Row className="g-3">
            {summaryData.map((item, idx) => (
              <Col
                key={idx}
                className="border-end last-child-border-0 text-center"
              >
                <div className="text-muted fs-11 mb-2">{item.label}</div>
                <div className="fw-bold text-black fs-13 transition-all">
                  {item.value}
                </div>
              </Col>
            ))}
          </Row>
        </div>

        <div className="d-flex justify-content-end gap-2 mb-3">
          <Badge
            bg="light"
            className="text-black border px-3 py-2 d-flex align-items-center"
            style={{
              borderRadius: "20px",
              fontSize: "12px",
              backgroundColor: "#F1F5F9",
              borderColor: "#E2E8F0",
              height: "40px",
            }}
          >
            <span className="text-muted me-2">Total cash:</span>
            <span className="fw-bold" style={{ color: "#1E40AF" }}>
              SGD {request.totalCash}
            </span>
          </Badge>
          <Badge
            bg="light"
            className="text-black border px-3 py-2 d-flex align-items-center"
            style={{
              borderRadius: "20px",
              fontSize: "12px",
              backgroundColor: "#F1F5F9",
              borderColor: "#E2E8F0",
              height: "40px",
            }}
          >
            <span className="text-muted me-2">Total Request:</span>
            <span className="fw-bold" style={{ color: "#1E40AF" }}>
              SGD {request.amount}
            </span>
          </Badge>
        </div>

        <div className="card shadow-none rounded-4 overflow-hidden border border-light mb-4">
          <DataTable
            columns={columns}
            data={request.denominations || []}
            showPagination={false}
            minWidth="100%"
          />
        </div>

        {(canShowActions || isSupervisorAndNotes) && (
          <div className="mb-4">
            <label className="text-muted fs-12 mb-1">Notes</label>
            {canShowActions ? (
              <textarea
                className="form-control"
                rows="3"
                placeholder="NOTES"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                style={{
                  resize: "none",
                  borderRadius: "8px",
                  borderColor: "#E2E8F0",
                }}
              />
            ) : (
              <div
                className="form-control bg-light"
                style={{
                  minHeight: "80px",
                  borderRadius: "8px",
                  borderColor: "#E2E8F0",
                }}
              >
                {request.notes}
              </div>
            )}
          </div>
        )}
      </div>
    </CommonModal>
  );
};

export default RequestInformationModal;
